import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { DSButton, DSIcon } from '../design-system/primitives';

// Single button that opens a dropdown of actions, reusing DSSplitButton's own
// menu classes (ds-split/ds-split__menu/ds-split__menu-item) without its paired
// main-button layout, since here every action is equally a "pick one" choice
// rather than one primary action plus secondary ones.
//
// Actions is an array of { label, onClick, disabled?, tone? }; falsy entries are
// dropped, so callers can inline permission checks (`isAdmin && {...}`).
//
// The panel is portalled to <body> and positioned `fixed` rather than left as an
// absolutely-positioned child of the button. An absolute child still counts toward
// the page's scrollable area, so a panel wider than the space beside its button
// stretched the document, which showed up as horizontal scrollbars and a header
// that shifted whenever the menu was open. Fixed elements contribute no scroll
// overflow, so the layout no longer moves - and it also frees the panel from any
// `overflow: hidden` ancestor. Cost of leaving the flow: the position has to be
// computed here, and recomputed on scroll/resize since it no longer tracks the
// button on its own.
const VIEWPORT_MARGIN = 8;
const BUTTON_GAP = 6;

// `compact` renders the trigger as an icon-only overflow control ("...") for rows and
// cards where a labelled "Actions" button would outweigh the actions it hides.
function ActionsMenu({ actions, label = 'Actions', compact = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState(null);
  const containerRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClickOutside = (event) => {
      // The panel lives outside containerRef now, so it has to be checked too -
      // otherwise mousedown on a menu item would unmount it before its click fired.
      const insideButton = containerRef.current && containerRef.current.contains(event.target);
      const insideMenu = menuRef.current && menuRef.current.contains(event.target);
      if (!insideButton && !insideMenu) setIsOpen(false);
    };
    const handleEscape = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  // Right-align the panel to the button - the usual placement is a right-hand corner
  // of a header row - then clamp into the viewport so it is never cut off, and flip
  // above the button when there isn't room below.
  const updatePosition = useCallback(() => {
    const menu = menuRef.current;
    const container = containerRef.current;
    if (!menu || !container) return;
    const anchor = container.getBoundingClientRect();
    const width = menu.offsetWidth;
    const height = menu.offsetHeight;

    const maxLeft = window.innerWidth - VIEWPORT_MARGIN - width;
    const left = Math.max(VIEWPORT_MARGIN, Math.min(anchor.right - width, maxLeft));

    const below = anchor.bottom + BUTTON_GAP;
    const above = anchor.top - BUTTON_GAP - height;
    const fitsBelow = below + height <= window.innerHeight - VIEWPORT_MARGIN;
    const top = fitsBelow || above < VIEWPORT_MARGIN ? below : above;

    setPosition({ top, left });
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) {
      setPosition(null);
      return undefined;
    }
    updatePosition();
    window.addEventListener('resize', updatePosition);
    // Capture phase so scrolling in any container, not just the window, keeps the
    // panel attached to its button.
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen, updatePosition]);

  const visibleActions = actions.filter(Boolean);
  if (visibleActions.length === 0) return null;

  const menu = (
    <div
      className="ds-split__menu ds-split__menu--floating"
      role="menu"
      ref={menuRef}
      style={{
        top: position ? `${position.top}px` : 0,
        left: position ? `${position.left}px` : 0,
        // First paint happens after the layout effect has measured, but guard the
        // pre-measurement frame anyway so the panel never flashes in the corner.
        visibility: position ? 'visible' : 'hidden'
      }}
    >
      {visibleActions.map((action) => (
        <button
          key={action.label}
          type="button"
          className={`ds-split__menu-item ${action.tone === 'danger' ? 'is-danger' : ''}`}
          onClick={() => {
            action.onClick();
            setIsOpen(false);
          }}
          disabled={action.disabled}
          role="menuitem"
        >
          {action.label}
        </button>
      ))}
    </div>
  );

  return (
    <div className="ds-split" ref={containerRef}>
      {compact ? (
        <DSButton
          appearance="outline"
          tone="neutral"
          iconOnly
          aria-label={label}
          aria-haspopup="menu"
          aria-expanded={isOpen}
          leadingIcon={<DSIcon name="more" size="sm" tone="default" />}
          onClick={() => setIsOpen(prev => !prev)}
        />
      ) : (
        <DSButton
          appearance="outline"
          tone="neutral"
          size="sm"
          trailingIcon={<DSIcon name="chevronDown" size="sm" tone="default" />}
          aria-haspopup="menu"
          aria-expanded={isOpen}
          onClick={() => setIsOpen(prev => !prev)}
        >
          {label}
        </DSButton>
      )}
      {isOpen && createPortal(menu, document.body)}
    </div>
  );
}

export default ActionsMenu;
